import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  PdfToolbarService
} from "@components/shared/document-viewer/pdf-toolbar/pdf-toolbar.service";
import {DomSanitizer} from "@angular/platform-browser";
import {DocumentTextHighlight} from "@components/shared/document-viewer/models/document-text-highlight";

@Component({
    selector: 'app-image-highlighter',
    templateUrl: './image-highlighter.component.html',
    styleUrls: ['./image-highlighter.component.scss'],
    standalone: false
})
export class ImageHighlighterComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() pageImgUrl = null;
  @Input() cropper = null;
  @Input() imagePlaceholderHeight: number = 1000;
  @Input() highlights: DocumentTextHighlight[] = [];
  @Input() activeHighlightId: string = null;
  @ViewChild('highlightedImgId') imageElement: ElementRef;
  @ViewChild('cropper') cropperElement: ElementRef;
  @Output() rerenderEvent = new EventEmitter<void>();
  @Output() highlightsRendered = new EventEmitter<void>();
  private resizeObserver?: ResizeObserver;
  private pendingHighlights = false;
  height = 0;
  width = 0;
  xcoord = 0;
  ycoord = 0;
  imageLoaded: boolean = false;
  imageFailedToLoad: boolean = false;
  imageHeight: number;
  imageWidth: number;
  actualImageAspectRatio: number;
  imageStyles: any;
  displayHighlights: Array<{
    id: string,
    left: number,
    top: number,
    width: number,
    height: number
  }> = [];

  constructor(
    private pdfToolbarService: PdfToolbarService,
    private sanitizer: DomSanitizer,
    private _cdr: ChangeDetectorRef,
    private _ngZone: NgZone
  ) {
  }

  ngOnInit() {
    this.loadImage();
  }

  /* The #highlightedImgId element only exists once imageLoaded is true (see the @if in the
    template), and images load asynchronously - so on first render this fires before the
    element exists, and the ResizeObserver never gets attached. Also try once here for the
    (rare) case the image was already loaded. loadImage()'s onload handler makes the real
    attach attempt once the element actually exists. */
  ngAfterViewInit() {
    this.attachResizeObserver();
  }

  /* This app's polyfills only load bare 'zone.js' (see angular.json), not the opt-in
    zone.js/plugins/zone-patch-resize-observer - so ResizeObserver callbacks run outside NgZone.
    Re-entering the zone here lets Angular schedule change detection through its normal
    mechanism, the same way the missing polyfill would have, instead of forcing a raw
    detectChanges() call on this view from an async callback - which can run interleaved with
    the split-pane drag handler's own detectChanges() in spreading-v2.component.ts and throw. */
  private attachResizeObserver(): void {
    if (this.resizeObserver || typeof ResizeObserver === 'undefined' || !this.imageElement) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.imageLoaded) {
        return;
      }
      this._ngZone.run(() => {
        this.setImageDimensions();
        this.resetHighlights();
        if (this.cropper) {
          this.resetCropper();
        }
      });
    });
    this.resizeObserver.observe(this.imageElement.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('cropper')) {
      if (this.imageLoaded) {
        // Ensure we are using the latest rendered image size (after any split resize / window resize)
        this.setImageDimensions();
        this.resetCropper();
      }
    }
    if (changes.hasOwnProperty('highlights')) {
      if (this.imageLoaded) {
        this.resetHighlights();
        this.highlightsRendered.emit();
      } else {
        this.pendingHighlights = true;
      }
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.unobserve(this.imageElement.nativeElement);
      this.resizeObserver.disconnect();
    }
  }

  loadImage() {
    const image = new Image();
    image.onload = () => {
      this.actualImageAspectRatio = image.height / image.width;
      this.setImageStyles();
      this.imageLoaded = true;
      this.rerenderEvent.emit();
      setTimeout(() => {
        this.attachResizeObserver();
        this.setImageDimensions();
        this.resetHighlights();
        if (this.pendingHighlights) {
          this.pendingHighlights = false;
          this._cdr.detectChanges();
          this.highlightsRendered.emit();
        } else {
          this._cdr.markForCheck();
        }
      })
      if (this.cropper) {
        setTimeout(() => {
          this.resetCropper();
          this.rerenderEvent.emit();
        });
      }
    }
    image.onerror = () => {
      this.imageFailedToLoad = true;
    }
    image.src = this.sanitizer.sanitize(SecurityContext.URL, this.pageImgUrl);
  }
  
  /* Defer the element lookup until after change detection re-creates the cropper. When the box
    was just removed (e.g. selecting a metadata cell clears viewSourcePage) and re-added, the
    @ViewChild is not yet resolved during ngOnChanges, so checking it before the setTimeout
    silently skips the scroll. */
  scrollCropperIntoView(){    
    setTimeout(() => {
      if (this.cropperElement) {
        this.cropperElement.nativeElement.scrollIntoView({behavior: 'auto', block: 'center', inline: 'center'});
      }
    });
  }

  setImageStyles() {
    this.imageStyles = {
      'background-image': this.sanitizer.sanitize(SecurityContext.STYLE, `url('${this.pageImgUrl}')`),
      'padding-top.%': this.actualImageAspectRatio * 100
    }
  }

  setImageDimensions() {
    // set image dimensions based on the current rendered size so highlight stays aligned
    const imageRect = this.imageElement.nativeElement.getBoundingClientRect();
    this.imageWidth = imageRect.width;
    this.imageHeight = imageRect.height;
    if (this.pdfToolbarService.imageScale !== 1) {
      // adjust image dimensions to account for zoom scale
      this.imageWidth = this.imageWidth / this.pdfToolbarService.imageScale;
      this.imageHeight = this.imageHeight / this.pdfToolbarService.imageScale;
    }
  }

  resetCropper() {
    if (!this.cropper || !this.imageElement) {
      return
    }
    const widthRatio = this.imageWidth / this.cropper.width;
    const heightRatio = this.imageHeight / this.cropper.height;

    // if width or height is 0, it is bc we intentionally do not want to show a box so dont add padding
    this.width = this.cropper.width !== 0 ? ((this.cropper.x2 - this.cropper.x1) * widthRatio) + 8 : 0;
    this.height = this.cropper.height !== 0 ? ((this.cropper.y2 - this.cropper.y1) * heightRatio) + 8 : 0;
    this.xcoord = (this.cropper.x1 * widthRatio) - 4;
    this.ycoord = (this.cropper.y1 * heightRatio) - 4;
    this.scrollCropperIntoView();
  }

  resetHighlights() {
    if (!this.imageElement) {
      return;
    }

    if (!this.highlights || !this.highlights.length) {
      this.displayHighlights = [];
      return;
    }

    this.setImageDimensions();
    this.displayHighlights = this.highlights.map(highlight => {
      const pageWidth = highlight.pageSize?.width || this.imageWidth || 1;
      const pageHeight = highlight.pageSize?.height || this.imageHeight || 1;
      const widthRatio = this.imageWidth / pageWidth;
      const heightRatio = this.imageHeight / pageHeight;
      return {
        id: highlight.id,
        left: highlight.x1 * widthRatio,
        top: highlight.y1 * heightRatio,
        width: (highlight.x2 - highlight.x1) * widthRatio,
        height: (highlight.y2 - highlight.y1) * heightRatio
      };
    });
  }

  trackHighlightById(index: number, item: { id: string }): string {
    return item.id;
  }
}
